// deps
import React, { MouseEventHandler, memo, useCallback, useEffect, useRef } from "react";
import classNames from "classnames";

import styles from "./Button.v4.scss";
export interface ButtonProps {
    children: React.ReactNode;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    className?: string;
    disabled?: boolean;
    progress?: boolean;
    focusOnMount?: boolean;
    accent?: string;
    "data-testid"?: string;
    ariaLabel?: string;
}

export enum BUTTON_ACCENT {
    RUSTY_ORANGE = "RUSTY_ORANGE",
    RUSTY_ORANGE_BORDER = "RUSTY_ORANGE_BORDER",
    SWIGGY_ORANGE = "SWIGGY_ORANGE",
    SWIGGY_ORANGE_PLAIN = "SWIGGY_ORANGE_PLAIN",
    SWIGGY_ORANGE_LARGE = "SWIGGY_ORANGE_LARGE",
    SWIGGY_ORANGE_TRANSPARENT = "SWIGGY_ORANGE_TRANSPARENT",
    BLACK_BORDER = "BLACK_BORDER",
    GREEN = "GREEN",
    GREEN_LARGE = "GREEN_LARGE",
    GREEN_BORDER = "GREEN_BORDER",
    GREEN_TRANSPARENT = "GREEN_TRANSPARENT",
    SWIGGY_GREY_TRANSPARENT = "GREY_TRANSPARENT",
    // GREEN LARGE
}

function Button(props: ButtonProps): React.ReactElement {
    const { disabled, progress, onClick, accent, focusOnMount } = props;
    const _buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        let timeOut: number;
        if (focusOnMount) {
            timeOut = window.setTimeout(() => {
                _buttonRef.current?.focus();
            }, 500);
        }
        return (): void => clearTimeout(timeOut);
    }, [_buttonRef, focusOnMount]);

    const buttonClass = classNames(
        styles.button,
        {
            [styles.orangeButton]: BUTTON_ACCENT.SWIGGY_ORANGE === accent,
            [styles.orangeButtonPlain]: BUTTON_ACCENT.SWIGGY_ORANGE_PLAIN === accent,
            [styles.orangeButtonLarge]: BUTTON_ACCENT.SWIGGY_ORANGE_LARGE === accent,
            [styles.rustyOrangeButton]: BUTTON_ACCENT.RUSTY_ORANGE === accent,
            [styles.rustyOrangeBorderButton]: BUTTON_ACCENT.RUSTY_ORANGE_BORDER === accent,
            [styles.greenBorderButton]: BUTTON_ACCENT.GREEN_BORDER === accent,
            [styles.greenButtonLarge]: BUTTON_ACCENT.GREEN_LARGE === accent,
            [styles.greenButtonTransparent]: BUTTON_ACCENT.GREEN_TRANSPARENT === accent,
            [styles.orangeButtonTransparent]: BUTTON_ACCENT.SWIGGY_ORANGE_TRANSPARENT === accent,
            [styles.greyButtonTransparent]: BUTTON_ACCENT.SWIGGY_GREY_TRANSPARENT === accent,
            [styles.buttonProgress]: props.progress,
            [styles.buttonDisabled]: props.disabled,
        },
        props.className,
    );

    const onBtnClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>): void => {
            event.stopPropagation();
            if (!disabled && !progress && typeof onClick === "function") {
                onClick(event);
            }
        },
        [disabled, onClick, progress],
    );

    return (
        <button
            className={buttonClass}
            onClick={onBtnClick}
            data-testid={props["data-testid"]}
            ref={_buttonRef}
            aria-label={props.ariaLabel}
        >
            {props.children}
        </button>
    );
}

export default memo<ButtonProps>(Button);
